import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { paymentAPI, nurseAPI } from '../services/api';
import { toast } from 'react-toastify';
import { paymentUtils } from '../utils/paymentUtils';

const ReschedulePaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [session, setSession] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get session IDs from URL params
        const params = new URLSearchParams(location.search);
        const stripeSessionId = params.get('session_id');
        console.log('URL params:', {
          full: location.search,
          stripeSessionId,
          allParams: Object.fromEntries(params.entries())
        });

        // Get payment data using Stripe session ID
        const paymentResponse = await paymentAPI.getPaymentBySessionId(stripeSessionId);
        console.log('Payment response:', paymentResponse);
        setPaymentData(paymentResponse);

        // Use the session ID from the payment response for nurse session
        const nurseSessionId = paymentResponse.metadata?.sessionId;
        console.log('Nurse session ID from payment:', nurseSessionId);

        if (!nurseSessionId) {
          throw new Error('No nurse session ID found in payment data');
        }

        // Get nurse session details using nurse session ID
        const sessionResponse = await nurseAPI.getNurseSession(nurseSessionId);
        console.log('Session response:', sessionResponse);
        
        if (sessionResponse && sessionResponse.session) {
          console.log('Setting session data:', sessionResponse.session);
          setSession(sessionResponse.session);
          // Set payment completed flag to allow access to nurse list/details
          paymentUtils.setPaymentCompleted();
        } else {
          throw new Error('Invalid session data received');
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(error.response?.data?.error || 'Failed to fetch session data');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, location]);

  const handleContinue = () => {
    // Log session data to debug
    console.log('Current session data:', session);
    console.log('Doctor ID:', session?.doctor?.id);
    console.log('Session ID:', session?.id);  // Changed from _id to id

    // Navigate to nurse details with session data for rescheduling
    if (session?.doctor?.id && session?.id) {  // Changed from _id to id
      console.log('Navigating to nurse details with data:', {
        nurseId: session.doctor.id,
        sessionData: {
          id: session.id,  // Changed from _id to id
          currentStartTime: session.startTime,
          currentEndTime: session.endTime,
          date: session.date
        }
      });
      navigate(`/nurses/details/${session.doctor.id}`, {
        state: {
          isRescheduling: true,
          sessionToReschedule: {
            id: session.id,
            currentStartTime: session.startTime,
            currentEndTime: session.endTime,
            date: session.date
          }
        }
      });
    } else {
      // Log why we're falling back
      console.error('Missing required session data:', {
        doctorId: session?.doctor?.id,
        sessionId: session?.id  // Changed from _id to id
      });
      toast.error('Could not find nurse details. Redirecting to nurse list.');
      navigate('/nurses/list');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Reschedule Payment Successful!
        </h2>

        <p className="text-gray-600 mb-8">
          Your payment has been processed successfully. You can now choose a new time for your session.
        </p>

        {session && (
          <div className="mb-8 text-left">
            <h3 className="text-lg font-semibold mb-4">Current Session Details:</h3>
            <div className="space-y-2">
              <p className="text-gray-600">
                <span className="font-medium">Date:</span> {formatDate(session.date)}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Current Time:</span> {session.startTime} - {session.endTime}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Nurse:</span> {session.doctor?.name}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <button
            onClick={handleContinue}
            className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Continue to Select New Time
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full text-gray-600 py-3 px-4 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReschedulePaymentSuccess;
